"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import {
  Menu,
  X,
  ChevronDown,
  ArrowRight,
  Gift,
  TrendingUp,
  Network,
  Layers,
  Sparkles,
} from "lucide-react";
import { ThemeToggle } from "@/components/theme-toggle";
import { useTheme } from "next-themes";
import { LanguageSwitcher } from "@/components/language-switcher";
import { DropdownMenu } from "./dropdown-menu";
import { cn } from "@/lib/utils";
import {
  getLocalizedPath,
  getLocalizedHome,
  getLocalizedPagePath,
} from "@/lib/locale-utils";
import { getSlugFromPage } from "@/lib/page-slugs";

interface PlanMenuItem {
  title: string;
  slug: string;
  url?: string;
}

function getIconForPlan(title: string): any {
  const titleLower = title.toLowerCase();
  if (titleLower.includes("binary") || titleLower.includes("binar")) return Network;
  if (titleLower.includes("matrix") || titleLower.includes("matrice")) return Layers;
  if (titleLower.includes("unilevel") || titleLower.includes("uninivel") || titleLower.includes("uniniveau")) return TrendingUp;
  if (titleLower.includes("generation") || titleLower.includes("generacion")) return Sparkles;
  if (titleLower.includes("gift") || titleLower.includes("regalo") || titleLower.includes("cadeau")) return Gift;
  return Network;
}

function buildPlanMenuItems(
  plans: PlanMenuItem[],
  locale: string,
  viewAllHref: string,
  viewAllTitle: string
): Array<{ title: string; href: string; icon: any }> {
  const items = plans.slice(0, 6).map((plan) => ({
    title: plan.title,
    href: plan.url || getLocalizedPath(plan.slug, locale),
    icon: getIconForPlan(plan.title),
  }));
  items.push({ title: viewAllTitle, href: viewAllHref, icon: ArrowRight });
  return items;
}

export function Header({ initialPlanMenuItems }: { initialPlanMenuItems?: PlanMenuItem[] } = {}) {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const t = useTranslations("navigation");
  const locale = useLocale();
  const pathname = usePathname();
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  const [planMenuItems, setPlanMenuItems] = React.useState<
    Array<{ title: string; href: string; icon: any }>
  >(() => {
    if (initialPlanMenuItems?.length) {
      return buildPlanMenuItems(
        initialPlanMenuItems,
        locale,
        getLocalizedPagePath("mlm-software-plans", locale),
        t("viewAllPlans")
      );
    }
    return [];
  });

  // When no SSG data: fetch plans for menu
  React.useEffect(() => {
    if (initialPlanMenuItems?.length) {
      setPlanMenuItems(
        buildPlanMenuItems(
          initialPlanMenuItems,
          locale,
          getLocalizedPagePath("mlm-software-plans", locale),
          t("viewAllPlans")
        )
      );
      return;
    }
    const fetchPlans = async () => {
      try {
        const res = await fetch(`/api/mlm-plans/menu?locale=${locale}`, { cache: "no-store" });
        if (res.ok) {
          const plans: PlanMenuItem[] = await res.json();
          setPlanMenuItems(
            buildPlanMenuItems(
              plans,
              locale,
              getLocalizedPagePath("mlm-software-plans", locale),
              t("viewAllPlans")
            )
          );
        } else {
          setPlanMenuItems([{ title: t("viewAllPlans"), href: getLocalizedPagePath("mlm-software-plans", locale), icon: ArrowRight }]);
        }
      } catch {
        setPlanMenuItems([{ title: t("viewAllPlans"), href: getLocalizedPagePath("mlm-software-plans", locale), icon: ArrowRight }]);
      }
    };
    fetchPlans();
  }, [locale, t, initialPlanMenuItems?.length]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const isActive = (path: string) => {
    // Check if it's a page with translated slugs (legal, privacy-policy, refund-policy, free-mlm-software-demo)
    const pageSlug = getSlugFromPage(path, locale);
    if (pageSlug) {
      const localizedPagePath = getLocalizedPagePath(path, locale);
      return (
        pathname === localizedPagePath ||
        pathname.startsWith(localizedPagePath + "/")
      );
    }
    // For regular paths, use the standard localized path
    const localizedPath = getLocalizedPath(path, locale);
    return (
      pathname === localizedPath || pathname.startsWith(localizedPath + "/")
    );
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-200 dark:border-gray-900 bg-white/80 dark:bg-black/80 backdrop-blur-xl supports-[backdrop-filter]:bg-white/70 dark:supports-[backdrop-filter]:bg-black/70">
      <nav className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between ">
          {/* Logo */}
          <Link
            href={getLocalizedHome(locale)}
            title="Business MLM Software"
            className="flex items-center group/logo transition-transform duration-200 hover:scale-105"
          >
            <span className="relative block w-[180px] h-[40px]">
              <Image
                src={
                  mounted && resolvedTheme === "dark"
                    ? "/images/common/business-logo-white.png"
                    : "/images/common/business-logo.webp"
                }
                alt="Business MLM Software"
                fill
                className="object-contain"
                loading="eager"
                quality={70}
                sizes="180px"
              />
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            <Link
              href={getLocalizedPagePath("mlm-software-features", locale)}
              className={cn(
                "px-2 py-2 text-sm font-medium transition-colors duration-200",
                isActive("mlm-software-features")
                  ? "text-primary-600 dark:text-primary-400"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white",
              )}
            >
              {t("features")}
            </Link>

            <Link
              href={getLocalizedPagePath("free-mlm-software-demo", locale)}
              className={cn(
                "px-2 py-2 text-sm font-medium transition-colors duration-200",
                isActive("free-mlm-software-demo")
                  ? "text-primary-600 dark:text-primary-400"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white",
              )}
            >
              {t("demo")}
            </Link>

            <Link
              href={getLocalizedPagePath("mlm-software-modules", locale)}
              className={cn(
                "px-2 py-2 text-sm font-medium transition-colors duration-200",
                isActive("mlm-software-modules")
                  ? "text-primary-600 dark:text-primary-400"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white",
              )}
            >
              {t("modules")}
            </Link>

            <DropdownMenu
              title={t("mlmPlans")}
              href={getLocalizedPagePath("mlm-software-plans", locale)}
              items={planMenuItems}
              isActive={
                isActive("mlm-software-plans") || isActive("mlm-software-plans")
              }
            />

            <Link
              href={getLocalizedPagePath("mlm-software-services", locale)}
              className={cn(
                "px-2 py-2 text-sm font-medium transition-colors duration-200",
                isActive("mlm-software-services")
                  ? "text-primary-600 dark:text-primary-400"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white",
              )}
            >
              {t("services")}
            </Link>

            <Link
              href={getLocalizedPagePath("contact", locale)}
              className={cn(
                "px-2 py-2 text-sm font-medium transition-colors duration-200",
                isActive("contact")
                  ? "text-primary-600 dark:text-primary-400"
                  : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white",
              )}
            >
              {t("contact")}
            </Link>
          </div>

          {/* Right side actions */}
          <div className="flex items-center space-x-2">
            <Link
              href={getLocalizedPagePath("pricing", locale)}
              className={cn(
                "hidden md:flex items-center px-4 py-2 h-[40px] rounded-md bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-medium hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors",
              )}
            >
              Pricing
            </Link>
            {!pathname.startsWith("/mlm-companies") && <LanguageSwitcher />}
            <ThemeToggle />

            {/* Mobile menu button */}
            <button
              onClick={toggleMenu}
              className="lg:hidden px-1 py-2 text-gray-700 dark:text-gray-300"
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 dark:border-gray-800 py-4 animate-in slide-in-from-top-2 fade-in duration-200">
            <div className="flex flex-col space-y-4">
              <Link
                href={getLocalizedPagePath("mlm-software-features", locale)}
                onClick={toggleMenu}
                className={cn(
                  "text-sm font-medium px-2 py-1 rounded-lg transition-colors",
                  isActive("mlm-software-features")
                    ? "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20"
                    : "text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20",
                )}
              >
                {t("features")}
              </Link>

              <Link
                href={getLocalizedPagePath("free-mlm-software-demo", locale)}
                onClick={toggleMenu}
                className={cn(
                  "text-sm font-medium px-2 py-1 rounded-lg transition-colors",
                  isActive("free-mlm-software-demo")
                    ? "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20"
                    : "text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20",
                )}
              >
                {t("demo")}
              </Link>

              <Link
                href={getLocalizedPagePath("mlm-software-modules", locale)}
                onClick={toggleMenu}
                className={cn(
                  "text-sm font-medium px-2 py-1 rounded-lg transition-colors",
                  isActive("mlm-software-modules")
                    ? "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20"
                    : "text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20",
                )}
              >
                {t("modules")}
              </Link>

              {/* Mobile Dropdown for MLM Plans */}
              <MobileDropdown
                title={t("mlmPlans")}
                href={getLocalizedPagePath("mlm-software-plans", locale)}
                items={planMenuItems}
                onClose={toggleMenu}
              />

              <Link
                href={getLocalizedPagePath("mlm-software-services", locale)}
                onClick={toggleMenu}
                className={cn(
                  "text-sm font-medium px-2 py-1 rounded-lg transition-colors",
                  isActive("mlm-software-services")
                    ? "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20"
                    : "text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20",
                )}
              >
                {t("services")}
              </Link>

              <Link
                href={getLocalizedPagePath("contact", locale)}
                onClick={toggleMenu}
                className={cn(
                  "text-sm font-medium px-2 py-1 rounded-lg transition-colors",
                  isActive("contact")
                    ? "text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20"
                    : "text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20",
                )}
              >
                {t("contact")}
              </Link>

              <Link
                href={getLocalizedPagePath("pricing", locale)}
                onClick={toggleMenu}
                className={cn(
                  "text-sm font-medium px-2 py-3 text-center rounded-lg transition-colors bg-primary-600 dark:bg-primary-500 text-white hover:bg-primary-700 dark:hover:bg-primary-600",
                  isActive("pricing")
                    ? "bg-primary-700 dark:bg-primary-600"
                    : "",
                )}
              >
                Pricing
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}

// Mobile Dropdown Component
function MobileDropdown({
  title,
  href,
  items,
  onClose,
}: {
  title: string;
  href: string;
  items: Array<{
    title: string;
    href: string;
    icon?: React.ComponentType<{ className?: string }>;
  }>;
  onClose: () => void;
}) {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 px-2 py-1 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
      >
        <span>{title}</span>
        <ChevronDown
          className={cn(
            "h-4 w-4 transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>
      {isOpen && (
        <div className="ml-4 mt-2 space-y-2 animate-in slide-in-from-top-2 fade-in duration-200">
          {items.map((item, index) => {
            const ItemIcon = item.icon || ArrowRight;
            const isLastItem = index === items.length - 1;
            return (
              <Link
                key={index}
                href={item.href}
                onClick={onClose}
                className={cn(
                  "flex items-center gap-2 text-sm px-2 py-1 rounded-lg transition-colors",
                  isLastItem
                    ? "border-t border-gray-200 dark:border-gray-800 py-3 mt-2 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 font-semibold hover:bg-primary-100 dark:hover:bg-primary-900/30"
                    : "text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20",
                )}
              >
                <ItemIcon
                  className={cn(
                    "h-4 w-4",
                    isLastItem
                      ? "text-primary-600 dark:text-primary-400"
                      : "text-gray-500 dark:text-gray-400",
                  )}
                />
                <span>{item.title}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
